import React, { Fragment } from 'react'
import styled, { css } from 'styled-components'
import { Link } from '@reach/router'

// utils and config
import th from 'utils/themeHelper'
import constants from 'config/constants'

// Load components synchronously
import Tooltip from 'components/Tooltip'

// icons
import { CalendarEventFill } from '@styled-icons/bootstrap/CalendarEventFill'
import { CheckBox, CheckBoxOutlineBlank } from '@styled-icons/material'
import { Clock } from '@styled-icons/fa-regular/Clock'
import { CreativeCommonsZero } from '@styled-icons/fa-brands/CreativeCommonsZero'
import { Cancel } from '@styled-icons/material/Cancel'
import { AdminPanelSettings } from '@styled-icons/material/AdminPanelSettings'
import { BookmarkCheckFill } from '@styled-icons/bootstrap/BookmarkCheckFill'
import { StopwatchFill } from '@styled-icons/bootstrap/StopwatchFill'
import { ShoePrints } from '@styled-icons/fa-solid/ShoePrints'
import { Lock } from '@styled-icons/boxicons-solid/Lock'
import { DocumentPerson } from '@styled-icons/fluentui-system-filled/DocumentPerson'
import { HouseDoorFill } from '@styled-icons/bootstrap/HouseDoorFill'
import { Check } from '@styled-icons/material/Check'
import { Close } from '@styled-icons/evil/Close'
import { BodyText } from '@styled-icons/bootstrap/BodyText'
import { PeopleFill } from '@styled-icons/bootstrap'
import { PersonWorkspace } from '@styled-icons/bootstrap'
import { CartCheckFill } from '@styled-icons/bootstrap'
import { BuildingHouse } from '@styled-icons/boxicons-regular/BuildingHouse'
import { PercentOutline } from '@styled-icons/evaicons-outline/PercentOutline'
import { DollarCircle } from '@styled-icons/boxicons-regular/DollarCircle'
import { GraphBar } from '@styled-icons/foundation/GraphBar'
import { RatioOneToOne } from '@styled-icons/fluentui-system-regular/RatioOneToOne'
import { TextLeft } from '@styled-icons/bootstrap/TextLeft'
import { CheckboxBlankCircle } from '@styled-icons/remix-line/CheckboxBlankCircle'
import { PlusSign } from 'components/Icons/Icons'
import { FileInvoiceDollar } from '@styled-icons/fa-solid/FileInvoiceDollar'
import { Pencil } from '@styled-icons/evil/Pencil'
import { Results } from '@styled-icons/foundation/Results'
import { Mobile } from '@styled-icons/entypo/Mobile'
import { Home } from '@styled-icons/boxicons-regular/Home'
import { ChevronDown } from '@styled-icons/bootstrap/ChevronDown'
import { ChevronUp } from '@styled-icons/bootstrap/ChevronUp'
import { GraduationCap } from '@styled-icons/fa-solid/GraduationCap'

const primaryBaseFaded = '#379B75BB'
const secondaryBaseFaded = '#8CCA1155'

const lowercaseValue = value => {
  if (value === null) return ''

  if (typeof value === 'undefined') return ''
  if (typeof value === 'number') return `${value}`
  if (typeof value === 'boolean') return `${value}`
  if (typeof value === 'object') return ''

  return value.toLowerCase()
}

const getMultiPillsPillColor = ({ bgColor, color, item, columnKey }) => {
  const lowercased = lowercaseValue(item)

  if (columnKey === 'zero_rating_status') {
    const isApproved = !!lowercased.match('zero-rated')
    const isInvalid = !!lowercased.match('invalid')
    const isPending = !!lowercased.match('pending')
    const verificationRequired = !!lowercased.match('pbo verification required')

    const multiPillBgColor = isApproved
      ? th('secondary.dark')
      : isInvalid
        ? th('backgrounds.warning')
        : isPending
          ? th('primary.orange')
          : verificationRequired
            ? th('backgrounds.warning')
            : 'white'
    const multiPillColor = isApproved
      ? 'white'
      : isInvalid
        ? 'white'
        : isPending
          ? 'white'
          : verificationRequired
            ? 'white'
            : th('text.dark')

    return { multiPillBgColor, multiPillColor }
  }

  const isTimekeeperCol = ['break_summary', 'clock_in_summary', 'clock_out_summary'].includes(
    columnKey
  )

  if (isTimekeeperCol) {
    const isApproved = !!lowercased.match('approved')
    const isMissing = !!lowercased.match('n/a')
    const isTimeCounter = !!lowercased.match(/\d\dm/) || !!lowercased.match(/\d\d?h\s\d\d?m/)

    const multiPillBgColor = isTimeCounter
      ? th('backgrounds.violet')
      : isApproved
        ? th('secondary.dark')
        : isMissing
          ? th('backgrounds.mid')
          : th('backgrounds.seaBreeze')
    const multiPillColor = isApproved ? 'white' : th('text.dark')

    return { multiPillBgColor, multiPillColor }
  }

  if (columnKey === 'websites_to_handle') {
    const isZeroRated = !!lowercased.match('zero-rated') && !lowercased.match('0 zero-rated')
    const isRejected = !!lowercased.match('rejected')
    const hasNonPending = lowercased.match('0 pending')
    const hasNonRejected = lowercased.match('0 rejected')
    const isPending = !!lowercased.match('pending')

    const multiPillBgColor = isZeroRated
      ? th('secondary.dark')
      : hasNonRejected
        ? th('backgrounds.mid')
        : isRejected
          ? th('backgrounds.warning')
          : hasNonPending
            ? th('backgrounds.mid')
            : isPending
              ? th('primary.orange')
              : 'white'
    const multiPillColor = isZeroRated
      ? 'white'
      : hasNonRejected
        ? th('text.dark')
        : isRejected
          ? 'white'
          : hasNonPending
            ? th('text.dark')
            : isPending
              ? 'white'
              : th('text.dark')

    return { multiPillBgColor, multiPillColor }
  }

  if (columnKey === 'website_statuses') {
    const isApproved = !!lowercased.match('approved') || !!lowercased.match('listed')
    const isInvalid = !!lowercased.match('invalid')
    const isPending = !!lowercased.match('pending')

    const multiPillBgColor = isApproved
      ? th('secondary.dark')
      : isInvalid
        ? th('backgrounds.warning')
        : isPending
          ? th('primary.orange')
          : 'white'
    const multiPillColor = isApproved
      ? 'white'
      : isInvalid
        ? 'white'
        : isPending
          ? 'white'
          : th('text.dark')

    return { multiPillBgColor, multiPillColor }
  }

  if (columnKey === 'budget_categories') {
    const multiPillBgColor = 'transparent'
    const multiPillColor = th('text.dark')

    return { multiPillBgColor, multiPillColor }
  }

  return { multiPillBgColor: bgColor, multiPillColor: color }
}

export const getPillColors = ({ headerMode, key, value }) => {
  const lowercased = lowercaseValue(value)
  const isPrimaryPill = ['group_name', 'province'].includes(key)

  if (headerMode === 'icon-headers-mode' && key === 'agreement_context_type') {
    return { bgColor: 'transparent', color: th('text.dark') }
  }

  if (key === 'agreement_context_type') {
    const isSkillLevel = !!lowercased.match('skill level')

    if (isSkillLevel) {
      return { bgColor: th('backgrounds.seaBreeze'), color: th('text.dark') }
    }

    return { bgColor: 'transparent', color: th('text.dark') }
  }

  if (key === 'purchase_request_batch_status') {
    if (lowercased.match('submitted')) {
      return { bgColor: th('primary.dark'), color: 'white' }
    }
    if (lowercased.match('rejected')) {
      return { bgColor: th('backgrounds.warning'), color: 'white' }
    }
    if (lowercased.match('initially approved')) {
      return { bgColor: th('primary.light'), color: th('text.dark') }
    }
    if (lowercased.match('approved for batch payment')) {
      return { bgColor: th('primary.base'), color: 'white' }
    }
    if (lowercased.match('payment executed')) {
      return { bgColor: th('primary.orange'), color: 'white' }
    }
    if (lowercased.match('success')) {
      return { bgColor: 'gold', color: th('text.dark') }
    }

    return { bgColor: th('backgrounds.seaBreeze'), color: th('text.dark') }
  }

  if (key === 'proof_status') {
    const isApproved = !!lowercased.match('approved')
    const isRejected = !!lowercased.match('rejected')

    if (isApproved) {
      return { bgColor: th('primary.base'), color: 'white' }
    }
    if (isRejected) {
      return { bgColor: th('backgrounds.warning'), color: 'white' }
    }

    return { bgColor: th('backgrounds.seaBreeze'), color: th('text.dark') }
  }

  if (key === 'shift_entry_type') {
    if (lowercased === 'start') {
      return { bgColor: th('primary.dark'), color: 'white' }
    }

    if (lowercased === 'end') {
      return { bgColor: th('backgrounds.warning'), color: 'white' }
    }

    return { bgColor: 'transparent', color: th('text.dark') }
  }

  if (key === 'shift_submission_is_approved') {
    if (lowercased === 'approved') {
      return { bgColor: th('primary.dark'), color: 'white' }
    }

    if (lowercased === 'pending') {
      return { bgColor: th('backgrounds.warning'), color: 'white' }
    }

    if (lowercased === 'tk approved') {
      return { bgColor: th('primary.orange'), color: 'white' }
    }

    return { bgColor: 'transparent', color: th('text.dark') }
  }

  if (key === 'outputs_tracking_status') {
    const overdue = !!lowercased.match('overdue')
    const upToDate = !!lowercased.match('up-to-date')
    const noIndicators = !!lowercased.match('no-indicators')
    const noOutputs = !!lowercased.match('no-outputs')

    const bgColor = upToDate
      ? th('primary.base')
      : overdue
        ? th('backgrounds.warning')
        : noOutputs
          ? th('primary.orange')
          : noIndicators
            ? th('backgrounds.mid')
            : '#379B7599'
    const color = 'white'

    return { bgColor, color }
  }

  if (key === 'zero_rated_website_url') {
    return { bgColor: 'transparent', color: th('text.dark') }
  }

  if (key === 'primary_email' && !value) {
    return { bgColor: th('backgrounds.warning'), color: 'white' }
  }

  if (key === 'budget_category_visibility') {
    const isVisible = !!lowercased.match('visible')

    return {
      bgColor: isVisible ? th('primary.base') : th('backgrounds.mid'),
      color: isVisible ? 'white' : th('text.dark')
    }
  }

  if (key === 'pbo_verification_status') {
    const isVerified = !!lowercased.match('verified')
    const isUnverified = !!lowercased.match('unverified')
    const hasRequested = !!lowercased.match('requested')

    const bgColor = hasRequested
      ? th('primary.orange')
      : isUnverified
        ? th('backgrounds.warning')
        : isVerified
          ? th('secondary.dark')
          : 'white'
    const color = hasRequested
      ? 'white'
      : isVerified
        ? 'white'
        : isUnverified
          ? 'white'
          : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'salaried_rate_type') {
    const isDaily = lowercased === 'daily'
    if (isDaily) {
      return { bgColor: th('primary.base'), color: 'white' }
    }

    const isHourly = lowercased === 'hourly'
    if (isHourly) {
      return { bgColor: th('primary.light'), color: 'white' }
    }

    return { bgColor: 'white', color: th('text.dark') }
  }

  if (key === 'zero_rating_status') {
    const isZeroRated = lowercased === 'zero rated'
    if (isZeroRated) {
      return { bgColor: 'gold', color: th('text.dark') }
    }

    const isInvalid = lowercased === 'invalid'
    if (isInvalid) {
      return { bgColor: th('backgrounds.warning'), color: 'white' }
    }

    const isPending = lowercased === 'pending'
    if (isPending) {
      return { bgColor: th('primary.orange'), color: 'white' }
    }

    return { bgColor: 'white', color: th('text.dark') }
  }

  if (key === 'payroll_request_status') {
    const isApproved = lowercased === 'payment request initiated'
    if (isApproved) {
      return { bgColor: 'gold', color: th('text.dark') }
    }

    const isPendingExecution = lowercased === 'pending execution'
    if (isPendingExecution) {
      return { bgColor: th('primary.base'), color: 'white' }
    }

    const isRejected = lowercased === 'rejected'
    if (isRejected) {
      return { bgColor: th('backgrounds.warning'), color: 'white' }
    }

    const isPending = lowercased.match('pending')
    if (isPending) {
      return { bgColor: th('primary.orange'), color: 'white' }
    }

    return { bgColor: 'white', color: th('text.dark') }
  }

  if (key === 'budget_status') {
    const isApproved = lowercased === 'approved'
    if (isApproved) {
      return { bgColor: th('primary.base'), color: 'white' }
    }

    const isRejected = lowercased === 'rejected'
    if (isRejected) {
      return { bgColor: th('backgrounds.warning'), color: 'white' }
    }

    const isPending = lowercased.match('pending')
    if (isPending) {
      return { bgColor: th('primary.orange'), color: 'white' }
    }

    return { bgColor: 'white', color: th('text.dark') }
  }

  if (key === 'purchase_request_status') {
    const hasApproved = lowercased === 'approved'
    const hasDenied = lowercased === 'denied'
    const hasPaid = lowercased === 'paid'
    const hasCompleted = lowercased === 'completed'
    const hasSubmitted = lowercased === 'submitted'
    const bgColor = hasApproved
      ? th('secondary.base')
      : hasDenied
        ? th('backgrounds.warning')
        : hasPaid
          ? 'gold'
          : hasCompleted
            ? primaryBaseFaded
            : hasSubmitted
              ? primaryBaseFaded
              : th('borders.default')
    const color = hasApproved
      ? 'white'
      : hasSubmitted
        ? 'white'
        : hasCompleted
          ? 'white'
          : hasDenied
            ? 'white'
            : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'application_status') {
    const isDraft = lowercased === 'draft'
    const isPending = lowercased === 'pending'
    const isInvalid = lowercased === 'invalid'
    const hasRejected = lowercased === 'rejected'
    const isCompleted = lowercased === 'completed'

    const bgColor = isDraft
      ? th('borders.default')
      : isPending
        ? th('secondary.lightest')
        : hasRejected
          ? th('backgrounds.warning')
          : isInvalid
            ? th('backgrounds.warning')
            : isCompleted
              ? th('secondary.dark')
              : th('borders.default')
    const color = isPending ? th('text.dark') : isDraft ? th('text.dark') : 'white'

    return { bgColor, color }
  }

  if (key === 'agreement_template_status') {
    const isActive = lowercased === 'active'
    const isInative = lowercased === 'inactive'

    const bgColor = isActive
      ? th('secondary.dark')
      : isInative
        ? th('backgrounds.warning')
        : th('borders.default')
    const color = isActive ? 'white' : isInative ? 'white' : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'member_roles') {
    const isTimekeeper = lowercased === 'timekeeper'
    const isPartnerAdmin = lowercased === 'partner admin'
    const isWorksiteAdmin = lowercased === 'worksite admin'
    const isWorkspaceAdmin = lowercased === 'workspace admin'
    const bgColor = isWorkspaceAdmin
      ? 'gold'
      : isTimekeeper
        ? '#379B7566'
        : isPartnerAdmin
          ? '#379B7566'
          : isWorksiteAdmin
            ? '#379B7566'
            : th('borders.default')
    const color = isWorkspaceAdmin
      ? th('text.dark')
      : isTimekeeper
        ? th('text.dark')
        : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'is_onboarded') {
    const isGreen = lowercased === 'onboarded'
    const bgColor = isGreen ? primaryBaseFaded : th('borders.default')
    const color = isGreen ? 'white' : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'area_code') {
    return { bgColor: th('primary.base'), color: 'white' }
  }

  if (key === 'workspace_project_code') {
    return { bgColor: th('primary.base'), color: 'white' }
  }

  if (key === 'program_code') {
    return { bgColor: th('primary.base'), color: 'white' }
  }

  if (key === 'salaried_rate_type') {
    const isHourly = lowercased === 'hourly'
    const bgColor = isHourly ? primaryBaseFaded : th('borders.default')
    const color = isHourly ? 'white' : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'category') {
    return { bgColor: '#379B7566', color: th('text.dark') }
  }

  if (key === 'area_name') {
    return { bgColor: '#379B7566', color: th('text.dark') }
  }

  if (key === 'member_role') {
    return { bgColor: '#379B7566', color: th('text.dark') }
  }

  if (key === 'membership_status') {
    const hasJoined = lowercased === 'joined'
    const hasDeclined = lowercased === 'declined'
    const hasBeenApproved = lowercased === 'approved'
    const hasBeenRejected = lowercased === 'rejected'
    const hasBeenRemoved = lowercased === 'removed'
    const bgColor = hasJoined
      ? 'gold'
      : hasBeenApproved
        ? 'gold'
        : hasDeclined
          ? th('backgrounds.warning')
          : hasBeenRejected
            ? th('backgrounds.warning')
            : hasBeenRemoved
              ? th('backgrounds.warning')
              : th('borders.default')
    const color = hasDeclined
      ? 'white'
      : hasBeenRejected
        ? 'white'
        : hasBeenRemoved
          ? 'white'
          : th('text.dark')

    return { bgColor, color }
  }

  if (isPrimaryPill) {
    const bgColor = primaryBaseFaded
    const color = 'white'

    return { bgColor, color }
  }

  if (key === 'faq_status_pill') {
    const isPublic = lowercased === 'public'
    const bgColor = isPublic ? primaryBaseFaded : th('borders.default')
    const color = isPublic ? 'white' : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'subscription_status') {
    const bgColor =
      lowercased === 'active'
        ? primaryBaseFaded
        : lowercased === 'paused'
          ? th('borders.default')
          : lowercased === 'cancelled'
            ? th('backgrounds.dark')
            : th('borders.default')

    const color =
      lowercased === 'active'
        ? 'white'
        : lowercased === 'paused'
          ? th('text.dark')
          : lowercased === 'cancelled'
            ? 'white'
            : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'volunteering_status') {
    const isJoined = value === constants.APPLICATION_STATUS_COLUMN_NAMES.JOINED
    const isDone = value === constants.APPLICATION_STATUS_COLUMN_NAMES.PARTICIPATED
    const isRed = [
      constants.APPLICATION_STATUS_COLUMN_NAMES.SKIPPED,
      constants.APPLICATION_STATUS_COLUMN_NAMES.CANCELLED
    ].includes(value)

    const bgColor = isJoined
      ? th('transparent.base')
      : isDone
        ? th('primary.base')
        : isRed
          ? th('backgrounds.warning')
          : th('transparent.base')

    const color = isJoined ? th('text.dark') : isDone ? 'white' : isRed ? 'white' : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'contribution_status') {
    const bgColor =
      lowercased === 'withdrew' || lowercased === 'rejected'
        ? th('backgrounds.warning')
        : lowercased === 'pending' || lowercased === 'pendingdonation'
          ? primaryBaseFaded
          : lowercased === 'pending approval'
            ? secondaryBaseFaded
            : lowercased === 'committed'
              ? th('borders.default')
              : lowercased === 'confirmed'
                ? 'gold'
                : th('borders.default')

    const color =
      lowercased === 'withdrew' || lowercased === 'rejected'
        ? 'white'
        : lowercased === 'pending' || lowercased === 'pendingdonation'
          ? 'white'
          : lowercased === 'pending approval'
            ? th('text.dark')
            : lowercased === 'confirmed'
              ? th('text.dark')
              : lowercased === 'committed'
                ? th('text.dark')
                : 'white'

    return { bgColor, color }
  }

  if (key === 'status') {
    const bgColor = ['active', 'pending round'].includes(lowercased)
      ? primaryBaseFaded
      : lowercased === 'withdrew'
        ? th('backgrounds.warning')
        : lowercased === 'skipped'
          ? th('primary.orange')
          : lowercased === 'draft'
            ? th('secondary.lightest')
            : ['completed', 'confirmed', 'winner'].includes(lowercased)
              ? 'gold'
              : th('borders.default')

    const color = ['active', 'pending round', 'skipped', 'withdrew'].includes(lowercased)
      ? 'white'
      : lowercased === 'draft'
        ? th('text.dark')
        : lowercased === 'completed'
          ? th('text.dark')
          : lowercased === 'confirmed'
            ? th('text.dark')
            : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'is_missing_details') {
    const bgColor = lowercased === 'missing info' ? th('backgrounds.warning') : 'gold'
    const color = lowercased === 'missing info' ? 'white' : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'org_type') {
    const bgColor = ['nonprofit', 'pbo'].includes(lowercased)
      ? 'gold'
      : lowercased === 'business'
        ? th('backgrounds.violet')
        : lowercased === 'school'
          ? th('backgrounds.seaBreeze')
          : th('borders.default')
    const color = th('text.dark')

    return { bgColor, color }
  }

  if (key === 'story_status') {
    const bgColor =
      lowercased === constants.STORY_STATES.PUBLIC.toLowerCase()
        ? primaryBaseFaded
        : ['action only', 'title only'].includes(lowercased)
          ? '#379B7566'
          : th('borders.default')

    const color =
      lowercased === constants.STORY_STATES.PUBLIC.toLowerCase()
        ? 'white'
        : ['action only', 'title only'].includes(lowercased)
          ? th('text.dark')
          : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'visibility_status') {
    const bgColor =
      lowercased === constants.PERSONAL_GOAL_VISIBILITY_OPTS.PUBLIC.toLowerCase()
        ? primaryBaseFaded
        : ['action only', 'title only'].includes(lowercased)
          ? '#379B7566'
          : th('borders.default')

    const color =
      lowercased === constants.PERSONAL_GOAL_VISIBILITY_OPTS.PUBLIC.toLowerCase()
        ? 'white'
        : ['action only', 'title only'].includes(lowercased)
          ? th('text.dark')
          : th('text.dark')

    return { bgColor, color }
  }

  if (key === 'visibility_status_pill') {
    const bgColor = lowercased === 'public' ? primaryBaseFaded : th('primary.orange')

    return { bgColor, color: 'white' }
  }

  if (key === 'payroll_status') {
    const bgColor =
      lowercased === 'nothing due'
        ? th('backgrounds.mid')
        : lowercased === 'unpaid'
          ? th('backgrounds.warning')
          : lowercased === 'ongoing payment pending'
            ? th('primary.dark')
            : lowercased.match('failed')
              ? th('backgrounds.warning')
              : primaryBaseFaded
    const color = lowercased === 'nothing due' ? th('text.dark') : 'white'

    return { bgColor, color }
  }

  if (key === 'supplier_status') {
    const bgColor =
      lowercased === 'pending'
        ? th('primary.orange')
        : lowercased === 'rejected'
          ? th('backgrounds.warning')
          : lowercased === 'approved'
            ? th('primary.base')
            : lowercased === 'rescinded'
              ? 'blueviolet'
              : th('backgrounds.warning')

    return { bgColor, color: 'white' }
  }

  if (key === 'total_payable_string') {
    // return { bgColor: th('borders.default'), color: th('text.dark') }

    const bgColor = lowercased === 'r0.00' ? secondaryBaseFaded : th('transparent.error')
    // const color = lowercased === 'n/a' ? th('text.dark') : 'white'
    const color = th('text.dark')

    return { bgColor, color }
  }

  if (key === 'tracking_frequency_short' || key === 'tracking_frequency') {
    const bgColor = ['m', 'monthly'].includes(lowercased)
      ? th('primary.base')
      : th('primary.orange')

    return { bgColor, color: 'white' }
  }

  if (key === 'output_tracking_day') {
    // check weekday names
    const bgColor = lowercased.match('n/a')
      ? th('backgrounds.mid')
      : lowercased.match('day')
        ? '#E8A01D66'
        : '#379B7566'

    return { bgColor, color: th('text.dark') }
  }

  if (['sef_agreement_signed'].includes(key)) {
    const bgColor =
      lowercased === 'unsigned'
        ? '#E8A01D66'
        : lowercased === 'terminated'
          ? th('backgrounds.warning')
          : lowercased === 'defaulted'
            ? 'blueviolet'
            : lowercased === 'unknown'
              ? 'blueviolet'
              : primaryBaseFaded

    return { bgColor, color: lowercased === 'unsigned' ? th('text.dark') : 'white' }
  }

  if (
    [
      'purchase_requests_enabled',
      'outputs_enabled',
      'payroll_enabled',
      'participant_management_enabled',
      'document_management_enabled'
    ].includes(key)
  ) {
    const bgColor = lowercased === 'yes' ? primaryBaseFaded : th('backgrounds.warning')

    return { bgColor, color: 'white' }
  }

  if (key === 'mobile_time_summary') {
    const isApproved = !!lowercased.match('approved')
    const isMissing = !!lowercased.match('n/a')
    const isTimeCounter = !!lowercased.match(/\d\dm/) || !!lowercased.match(/\d\d?h\s\d\d?m/)

    const bgColor = isTimeCounter
      ? th('backgrounds.violet')
      : isApproved
        ? th('secondary.dark')
        : isMissing
          ? th('backgrounds.mid')
          : th('backgrounds.seaBreeze')
    const color = isApproved ? 'white' : th('text.dark')

    return { bgColor, color }
  }

  if (key.match(/_pill$/)) {
    const bgColor = primaryBaseFaded
    const color = 'white'

    return { bgColor, color }
  }

  return { bgColor: th('borders.default'), color: th('text.dark') }
}

const CustomStatusPill = styled.div``

const CustomStatusPillImg = styled.img`
  border-radius: 50%;
  filter: ${props => (props.value === 'Pending' ? 'grayscale(1)' : 'none')};
  height: 30px;
  width: 30px;
`

const CustomStatusPillImage = ({ url, value, src }) => {
  return (
    <a href={url} target="_blank">
      <CustomStatusPillImg url={url} value={value} src={src} />
    </a>
  )
}

export const ProjectAdminIcon = styled(BookmarkCheckFill)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 6px;
  width: 25px;
`

export const AdminPanelIcon = styled(AdminPanelSettings)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 2px;
  width: 25px;
`

export const PayrollIcon = styled(FileInvoiceDollar)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  padding: 5px;
`

export const PencilIcon = styled(Pencil)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  padding: 5px;
`

export const ResultsIcon = styled(Results)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  padding: 5px;
`

export const SvgStopWatchFill = styled(StopwatchFill)`
  color: ${th('borders.dark')};
  height: 25px;
  width: 25px;
`

export const SvgBuildingHouse = styled(BuildingHouse)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 4px;
  width: 25px;
`

export const ClockIcon = styled(Clock)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 4px;
  width: 25px;
`

export const SvgLock = styled(Lock)`
  background: white;
  border: 1px solid ${th('borders.medium')};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 18px !important;
  padding: 1px 2px 2px;
  position: absolute;
  right: -6px;
  top: -6px;
  width: 18px !important;
`

export const SvgDocumentPerson = styled(DocumentPerson)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 4px;
  width: 25px;
`

export const SvgPlusSign = styled(PlusSign)`
  border: 1px solid ${th('borders.medium')};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 4px;
  width: 25px;
`

export const SvgCheck = styled(Check)`
  border-radius: 50%;
  color: ${th('primary.base')};
  height: 25px;
  width: 25px;
`

export const SvgClose = styled(Close)`
  border-radius: 50%;
  color: ${th('borders.error')};
  height: 25px;
  width: 25px;
`

export const SvgHouseDoorFill = styled(HouseDoorFill)`
  color: ${th('borders.dark')};
  height: 25px;
  width: 25px;
  background: #f3702050;
  padding: 4px;
  border-radius: 50%;
`

export const SvgPeopleFill = styled(PeopleFill)`
  border: 1px solid ${th('borders.medium')};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 4px;
  width: 25px;
`

export const SvgPersonWorkspace = styled(PersonWorkspace)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 4px;
  width: 25px;
`

export const CartCheckIcon = styled(CartCheckFill)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 4px;
  width: 25px;
`

export const MobileIcon = styled(Mobile)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 6px;
  width: 25px;
`

export const GraduationCapIcon = styled(GraduationCap)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 6px;
  width: 25px;
`

export const HomeIcon = styled(Home)`
  background: ${props => props.background};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 6px;
  width: 25px;
`

export const SvgCheckboxBlankCircle = styled(CheckboxBlankCircle)`
  color: ${th('borders.medium')};
  height: 25px;
  width: 25px;
`

export const SvgShoePrints = styled(ShoePrints)`
  border: 1px solid ${th('borders.medium')};
  border-radius: 50%;
  color: ${th('borders.dark')};
  height: 25px;
  padding: 4px;
  width: 25px;
`

export const SvgIconCal = styled(CalendarEventFill)`
  color: ${th('borders.dark')};
  height: 25px;
  width: 25px;
`

export const SvgIconBodyText = styled(BodyText)`
  color: ${th('borders.dark')};
  height: 25px;
  width: 25px;
`

export const SvgIconCancel = styled(Cancel)`
  color: ${th('borders.dark')};
  height: 25px;
  width: 25px;
`

export const SvgIconZero = styled(CreativeCommonsZero)`
  color: ${th('borders.dark')};
  height: 25px;
  width: 25px;
`

export const Checked = styled(CheckBox)`
  color: ${th('borders.darkPill')};
  height: 25px;
  width: 25px;
`

export const Unchecked = styled(CheckBoxOutlineBlank).withConfig({
  shouldForwardProp: prop => !['isDisabled'].includes(prop)
})`
  color: ${props => (props.isDisabled ? th('borders.default') : th('borders.darkPill'))};
  height: 25px;
  width: 25px;
`

const determineCardFlex = ({ cardType, showFilters }) => {
  if (cardType === 'DashProgramCard') {
    return { default: '0 0 100%', w600: '0 0 calc(100% - 20px)', w969: '0 0 calc(100% - 20px)' }
  }

  if (showFilters === 'false') {
    return { default: '0 0 100%', w600: '0 0 calc(50% - 20px)', w969: '0 0 calc(25% - 20px)' }
  }

  return { default: '0 0 100%', w600: '0 0 calc(50% - 20px)', w969: '0 0 calc(33% - 20px)' }
}

const Table = styled.table`
  border-collapse: collapse;
  padding: 10px;
  table-layout: fixed;
`

export const DashHomeStatsTableRow = styled.tr.withConfig({
  shouldForwardProp: prop => !['isSticky'].includes(prop)
})`
  height: 50px;
  position: ${({ isSticky }) => (isSticky ? 'sticky' : 'relative')};
  top: 0;
  z-index: ${({ isSticky }) => (isSticky ? 2 : 1)};

  &:nth-child(odd) {
    background: ${th('backgrounds.light')};
  }

  @media screen and (min-width: 769px) {
    position: relative;
  }
`

export const DashStatTableSubTitle = styled.th`
  font-family: ${th('fonts.light')};
  font-size: 10px;
  left: 0;
  position: absolute;
  width: 100%;
`

export const DashStatTableHeading = styled.th.withConfig({
  shouldForwardProp: prop =>
    ![
      'collapse',
      'headerMode',
      'headerFontSize',
      'size',
      'primaryCell',
      'padding',
      'cellAlign',
      'center',
      'forceNoWrap',
      'useHeaderIcons'
    ].includes(prop)
})`
  font-family: ${th('fonts.bold')};
  font-size: ${({ headerFontSize }) => headerFontSize || '15px'};
  width: ${({ size, primaryCell }) =>
    ['collapse', 'small', 'smallButWrap'].includes(size) ? 'auto' : primaryCell ? '100%' : 'auto'};
  padding: ${({ padding, headerFontSize }) =>
    padding || (headerFontSize === '12px' ? '15px 10px' : '15px 6px')};
  position: relative;
  text-align: ${({ cellAlign, center }) =>
    cellAlign === 'right' ? 'right' : center ? 'center' : 'left'};
  text-transform: uppercase;
  white-space: ${({ forceNoWrap, useHeaderIcons, size }) =>
    forceNoWrap ? 'nowrap' : useHeaderIcons ? 'normal' : size === 'small' ? 'nowrap' : 'normal'};
`

const CustomStatusPillValue = styled.div.withConfig({
  shouldForwardProp: prop => !['bgColor', 'fontColor', 'customPillCount'].includes(prop)
})`
  background-color: ${({ bgColor }) => bgColor || 'white'};
  border-radius: 2px;
  color: ${({ fontColor }) => fontColor || th('text.dark')};
  line-height: 1;
  padding: ${({ customPillCount }) => (customPillCount === 1 ? '5px 4px' : '3px 4px 2px')};
`

const CustomMultiPill = styled.div.withConfig({
  shouldForwardProp: prop =>
    !['columnKey', 'customPillCount', 'uppercase', 'fullWidth'].includes(prop)
})`
  border: ${({ columnKey }) => (columnKey === 'allocation_entity' ? '1px solid #484848' : 'none')};
  display: inline-block;
  font-size: 9px;
  margin-bottom: 5px;
  padding: ${({ customPillCount }) => (customPillCount ? '5px 5px 0' : '5px 5px 0')};
  text-align: center;
  text-transform: ${({ uppercase }) => (uppercase ? 'uppercase' : 'inherit')};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
`

const MultiPill = styled.div.withConfig({
  shouldForwardProp: prop =>
    ![
      'bgColor',
      'columnKey',
      'fontColor',
      'cursor',
      'uppercase',
      'fullWidth',
      'pillWidth'
    ].includes(prop)
})`
  background: ${({ bgColor }) => bgColor || th('backgrounds.default')};
  border: ${({ columnKey }) => (columnKey === 'budget_categories' ? '1px solid #484848' : 'none')};
  color: ${({ fontColor }) => fontColor || th('text.dark')};
  cursor: ${({ cursor }) => cursor || 'default'};
  border-radius: 3px;
  display: inline-block;
  font-size: 9px;
  margin-right: 5px;
  padding: ${({ cursor }) => (cursor ? '0' : '2px 5px')};
  text-align: center;
  text-transform: ${({ uppercase }) => (uppercase ? 'uppercase' : 'inherit')};
  width: ${({ fullWidth, pillWidth }) => (fullWidth ? '100%' : pillWidth || 'auto')};
`

const Pill = styled.div.withConfig({
  shouldForwardProp: prop =>
    !['bgColor', 'fontColor', 'fontBold', 'uppercase', 'fullWidth'].includes(prop)
})`
  background: ${({ bgColor }) => bgColor || th('backgrounds.default')};
  color: ${({ fontColor }) => fontColor || th('text.dark')};
  border-radius: 3px;
  display: inline-block;
  font-family: ${({ fontBold }) => (fontBold ? th('fonts.bold') : th('fonts.regular'))};
  font-size: 9px;
  padding: 2px 5px;
  text-align: center;
  text-transform: ${({ uppercase }) => (uppercase ? 'uppercase' : 'inherit')};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};

  svg {
    width: 15px;
    height: 15px;
  }
`

const NullIconWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: ${props => (props.nullTextAlign === 'left' ? 'flex-start' : 'center')};

  img,
  svg {
    height: 20px;
    width: 20px;
    margin-right: 8px;
  }
`

export const HeaderIconWrapper = styled.div`
  svg {
    height: 20px;
    width: 20px;
  }
`

const IconWrapper = styled.div.withConfig({
  shouldForwardProp: prop => !['smallIcon'].includes(prop)
})`
  img {
    height: ${props => (props.smallIcon ? '10px' : '20px')};
    width: ${props => (props.smallIcon ? '10px' : '20px')};
  }

  svg {
    height: ${props => (props.smallIcon ? '10px' : '20px')};
    width: ${props => (props.smallIcon ? '10px' : '20px')};
  }

  @media screen and (min-width: 769px) {
    img {
      height: ${props => (props.smallIcon ? '15px' : '30px')};
      width: ${props => (props.smallIcon ? '15px' : '30px')};
    }

    svg {
      height: ${props => (props.smallIcon ? '15px' : '30px')};
      width: ${props => (props.smallIcon ? '15px' : '30px')};
    }
  }
`

const ColumnHint = styled.span`
  font-family: ${th('fonts.italic')};
`

const TableCellImage = styled.img.withConfig({
  shouldForwardProp: prop => !['round'].includes(prop)
})`
  border-radius: ${props => (props.round ? '50%' : '0')};
  height: 30px;
  width: 30px;
`

const TableCellSvgImage = styled.div`
  background: ${props => (props.avatarStyles ? props.avatarStyles.background : 'transparent')};
  border-color: ${props => (props.avatarStyles ? props.avatarStyles.borderColor : 'transparent')};
  border-radius: ${props => (props.avatarStyles ? props.avatarStyles.borderRadius : '0')};
  border-style: solid;
  border-width: ${props => (props.avatarStyles ? props.avatarStyles.borderWidth : '0')};
  height: ${props => (props.avatarStyles ? props.avatarStyles.height : '30px')};
  padding: ${props => (props.avatarStyles ? props.avatarStyles.padding : '0')};
  width: ${props => (props.avatarStyles ? props.avatarStyles.width : '30px')};
`

const NullText = styled.span`
  color: ${th('text.light')};
  font-family: ${th('fonts.italic')};
`

export const ActionIconHint = styled.span`
  font-size: 12px;
  position: absolute;
  right: -8px;
  top: -8px;
`

const ExternalLinkButtonStub = styled.div`
  align-items: center;
  background: ${th('backgrounds.mid')};
  border: 1px solid ${th('borders.default')};
  border-radius: 4px;
  color: ${th('text.dark')};
  display: flex;
  min-height: 35px;
  padding: 6px 12px;

  &:hover {
    background: ${th('transparent.error')};
    cursor: not-allowed;
  }
`

const ExternalLinkButtonComponent = styled.a.withConfig({
  shouldForwardProp: prop => !['overrideBorderColor'].includes(prop)
})`
  align-items: center;
  background: white;
  border: 1px solid ${props => props.overrideBorderColor || th('borders.default')};
  border-radius: 4px;
  color: ${th('text.dark')};
  cursor: pointer;
  display: flex;
  min-height: 35px;
  padding: 6px 12px;

  &:hover {
    background: ${th('primary.base')};
    border-color: ${th('borders.dark')};
    color: white;
  }
`

export const ExternalLinkButton = ({ children, disabled, href, overrideBorderColor, target }) => {
  if (disabled) {
    return <ExternalLinkButtonStub>{children}</ExternalLinkButtonStub>
  }

  return (
    <ExternalLinkButtonComponent
      href={href}
      overrideBorderColor={overrideBorderColor}
      target={target}
      rel="noopener noreferrer"
    >
      {children}
    </ExternalLinkButtonComponent>
  )
}

const CustomPillIconSelection = ({ item }) => {
  if (!item.iconName) return null

  switch (item.iconName) {
    case 'Onboarded':
      return <SvgCheck />
    case 'HasMoreRoles':
      return <SvgPlusSign />
    case 'Not onboarded':
      return <SvgClose />
    case constants.ROLES.GOD_MODE:
      return <AdminPanelIcon background={'#3BB2E2'} />
    case constants.ROLES.SEF_WORKSPACE_ADMIN:
      return <AdminPanelIcon background={'gold'} />
    case constants.ROLES.SEF_WORKSPACE_FINANCE_ADMIN:
      return <PayrollIcon background={'gold'} />
    case constants.ROLES.SEF_WORKSPACE_PROCUREMENT_ADMIN:
      return <CartCheckIcon background={'gold'} />
    case constants.ROLES.SEF_WORKSPACE_PROJECT_ADMIN:
      return <AdminPanelIcon background={'#E8A01D88'} />
    case constants.ROLES.SEF_WORKSPACE_PROJECT_SIGNING_ADMIN:
      return <PencilIcon background={'#E8A01D88'} />
    case constants.ROLES.SEF_WORKSPACE_SIGNING_ADMIN:
      return <PencilIcon background={'gold'} />
    case constants.ROLES.SEF_WORKSPACE_TIMEKEEPER_ADMIN:
      return <ClockIcon background={'gold'} />
    case constants.ROLES.SEF_PARTNER_ADMIN:
      return <HomeIcon background={'#379b7550'} />
    case constants.ROLES.SEF_PARTNER_TRAINING_ADMIN:
      return <GraduationCapIcon background={'#379b7550'} />
    case constants.ROLES.SEF_PARTNER_TIMEKEEPER_ADMIN:
      return <ClockIcon background={'#379b7550'} />
    case constants.ROLES.SUPERADMIN:
      return <HomeIcon background={'#379b7550'} />
    case constants.ROLES.SEF_PAYROLL_REVIEW_ADMIN:
      return <PayrollIcon background={'#FA7F7A'} />
    case constants.ROLES.SEF_WORKSITE_OUTPUTS_ADMIN:
      return <ResultsIcon background={'#F6E7B0'} />
    case constants.ROLES.SEF_PARTNER_DISTRICT_PEOPLE_ADMIN:
      return <SvgPersonWorkspace background={'#8CCA1188'} />
    case constants.ROLES.SEF_WORKSITE_PEOPLE_ADMIN:
      return <SvgPersonWorkspace background={'#8CCA1188'} />
    case constants.ROLES.SEF_PARTNER_DISTRICT_ADMIN:
      return <SvgPersonWorkspace background={'#8CCA1188'} />
    case constants.ROLES.DGMT_PBO_ADMIN:
      return <MobileIcon background={'#104D8C88'} />
    case 'Nonprofit Admin':
      return (
        <div style={{ position: 'relative' }}>
          <SvgBuildingHouse background="#379b7550" />
          <SvgLock />
        </div>
      )
    case constants.ROLES.SEF_PARTNER_DISTRICT_OUTPUTS_ADMIN:
      return <SvgDocumentPerson background="#8CCA1188" />
    case constants.ROLES.SEF_WORKSITE_DATA_COLLECTOR:
      return <MobileIcon background="#8CCA1188" />
    case constants.ROLES.MEMBER:
      return <SvgPeopleFill />
    case 'SefIndicatorPercentage':
      return <PercentOutline />
    case 'SefIndicatorMoney':
      return <DollarCircle />
    case 'SefIndicatorAbsolute':
      return <GraphBar />
    case 'SefIndicatorRatio':
      return <RatioOneToOne />
    case 'SefIndicatorText':
      return <TextLeft />
    case 'ClockIn':
      return <ClockIcon background="#379B7570" />
    case 'ClockOut':
      return <ClockIcon background="#E8A01D70" />
    case 'NoRole':
      return <SvgCheckboxBlankCircle />
    default:
      return <SvgShoePrints />
  }
}

const CustomPillIconSection = ({ item, smallIcon }) => {
  if (!item) return null

  return (
    <IconWrapper smallIcon={smallIcon}>
      <CustomPillIconSelection item={item} />
    </IconWrapper>
  )
}

export const DashHomeStatsTable = ({ children }) => {
  return (
    <Table>
      <tbody>{children}</tbody>
    </Table>
  )
}

export const DashStatTableValueInner = ({
  avatarStyles,
  bgColor,
  canEditCheckbox,
  color,
  columnKey,
  cursor,
  fontBold,
  fullWidth,
  hoverLabel,
  icon: Icon,
  id,
  image,
  isCheckbox,
  isCustomStatusPill,
  isLink,
  isMultiStatusPill,
  isStatusPill,
  link,
  nullIcon: NullIcon,
  nullPillText,
  nullText,
  nullTextAlign,
  pillWidth,
  showCheckbox,
  smallIcon,
  toggleCheckbox,
  tooltipPosition,
  trimText,
  translateX,
  translateY,
  uppercase,
  value
}) => {
  if (isLink && link) {
    return (
      <DashStatTableValueLink to={link} trimText={trimText}>
        {value}
      </DashStatTableValueLink>
    )
  }

  if (image && hoverLabel) {
    if (typeof image === 'object') {
      const SvgImage = image
      return (
        <Tooltip content={hoverLabel} forceShow position="right" tinyTooltip useAutoWidth>
          <TableCellSvgImage avatarStyles={avatarStyles}>
            <SvgImage />
          </TableCellSvgImage>
        </Tooltip>
      )
    }

    return (
      <Tooltip content={hoverLabel} forceShow position="right" tinyTooltip useAutoWidth>
        <TableCellImage round src={image}></TableCellImage>
      </Tooltip>
    )
  }

  if (image) {
    if (typeof image === 'object') {
      const SvgImage = image
      return (
        <TableCellSvgImage avatarStyles={avatarStyles}>
          <SvgImage />
        </TableCellSvgImage>
      )
    }

    return <TableCellImage round src={image}></TableCellImage>
  }

  const isSvgIconCal = Icon === 'CalendarEventFill'
  const isSvgIconZero = Icon === 'CreativeCommonsZero'
  const isSvgIconCancel = Icon === 'CancelIcon'
  const isBodyTextIcon = Icon === 'BodyTextIcon'
  const isSvgIcon = [
    'CalendarEventFill',
    'CreativeCommonsZero',
    'CancelIcon',
    'BodyTextIcon'
  ].includes(Icon)

  if (isSvgIcon && hoverLabel) {
    return (
      <Tooltip content={hoverLabel} forceShow position="right" tinyTooltip useAutoWidth>
        <IconWrapper smallIcon={smallIcon}>
          {isSvgIconCal ? (
            <SvgIconCal />
          ) : isSvgIconZero ? (
            <SvgIconZero />
          ) : isSvgIconCancel ? (
            <SvgIconCancel />
          ) : isBodyTextIcon ? (
            <SvgIconBodyText />
          ) : null}
        </IconWrapper>
      </Tooltip>
    )
  }

  if (isSvgIcon) {
    return (
      <IconWrapper smallIcon={smallIcon}>
        {isSvgIconCal ? (
          <SvgIconCal />
        ) : isSvgIconZero ? (
          <SvgIconZero />
        ) : isSvgIconCancel ? (
          <SvgIconCancel />
        ) : isBodyTextIcon ? (
          <SvgIconBodyText />
        ) : null}
      </IconWrapper>
    )
  }

  if (Icon && hoverLabel) {
    return (
      <Tooltip content={hoverLabel} forceShow position="right" tinyTooltip useAutoWidth>
        <IconWrapper smallIcon={smallIcon}>
          <TableCellImage src={Icon} />
        </IconWrapper>
      </Tooltip>
    )
  }

  if (Icon) {
    return (
      <IconWrapper smallIcon={smallIcon}>
        <TableCellImage src={Icon} />
      </IconWrapper>
    )
  }

  if (!value && NullIcon) {
    return (
      <NullIconWrapper nullTextAlign={nullTextAlign}>
        <NullIcon />
        <NullText>{nullText || ''}</NullText>
      </NullIconWrapper>
    )
  }

  if (!value && nullText) {
    return (
      <NullIconWrapper nullTextAlign={nullTextAlign}>
        <NullText>{nullText}</NullText>
      </NullIconWrapper>
    )
  }

  if (!value && nullPillText) {
    return (
      <Pill
        bgColor={bgColor}
        fontBold={fontBold}
        fontColor={color}
        fullWidth={fullWidth}
        value={nullPillText}
        uppercase
      >
        {nullPillText}
      </Pill>
    )
  }

  if (isCheckbox && showCheckbox) {
    const isDisabled = !canEditCheckbox

    if (value) {
      return <Checked onClick={() => toggleCheckbox(id)} />
    }

    return (
      <Unchecked isDisabled={isDisabled} onClick={() => (isDisabled ? null : toggleCheckbox(id))} />
    )
  }

  if (isCheckbox && showCheckbox) {
    return <span>{value ? 'Yes' : 'No'}</span>
  }

  if (isCheckbox) {
    return <ColumnHint>N/A</ColumnHint>
  }

  if (!value) return null

  if (isCustomStatusPill) {
    const customPillCount = value.length

    return value.map((item, index) => {
      const { multiPillBgColor, multiPillColor } = getMultiPillsPillColor({
        bgColor,
        color,
        item: item.value,
        columnKey
      })

      const hasNoValue = !item.value && !item.custom_pill_img_url && !item.custom_pill_icon

      if (item.tooltip) {
        return (
          <Tooltip
            key={`tooltip-${index}`}
            content={item.tooltip}
            forceShow
            position={tooltipPosition || 'right'}
            tinyTooltip
            translateX={translateX}
            translateY={translateY}
            useAutoWidth
          >
            <CustomMultiPill
              columnKey={columnKey}
              customPillCount={customPillCount}
              key={index}
              uppercase={uppercase}
              value={item.value}
              fullWidth={fullWidth}
            >
              <CustomStatusPill>
                {item.custom_pill_img_url && (
                  <CustomStatusPillImage
                    url={item.external_url}
                    value={item.value}
                    src={item.custom_pill_img_url}
                  />
                )}

                {item.custom_pill_icon && (
                  <CustomPillIconSection item={item} smallIcon={smallIcon} />
                )}

                {item.value && (
                  <CustomStatusPillValue
                    bgColor={multiPillBgColor}
                    columnKey={columnKey}
                    customPillCount={customPillCount}
                    fontColor={multiPillColor}
                  >
                    {item.value}
                  </CustomStatusPillValue>
                )}

                {hasNoValue && (
                  <NullIconWrapper>
                    <NullText>N/A</NullText>
                  </NullIconWrapper>
                )}
              </CustomStatusPill>
            </CustomMultiPill>
          </Tooltip>
        )
      }

      return (
        <CustomMultiPill
          columnKey={columnKey}
          customPillCount={customPillCount}
          key={index}
          uppercase={uppercase}
          value={item.value}
          fullWidth={fullWidth}
        >
          <CustomStatusPill>
            {item.custom_pill_img_url && (
              <CustomStatusPillImage
                url={item.external_url}
                value={item.value}
                src={item.custom_pill_img_url}
              />
            )}

            {item.custom_pill_icon && <CustomPillIconSection item={item} smallIcon={smallIcon} />}

            {item.value && (
              <CustomStatusPillValue
                bgColor={multiPillBgColor}
                customPillCount={customPillCount}
                columnKey={columnKey}
                fontColor={multiPillColor}
              >
                {item.value}
              </CustomStatusPillValue>
            )}

            {hasNoValue && (
              <NullIconWrapper>
                <NullText>{nullText || 'Missing or invalid value'}</NullText>
              </NullIconWrapper>
            )}
          </CustomStatusPill>
        </CustomMultiPill>
      )
    })
  }

  if (isMultiStatusPill) {
    return value.map((item, index) => {
      const { multiPillBgColor, multiPillColor } = getMultiPillsPillColor({
        bgColor,
        color,
        item,
        columnKey
      })

      return (
        <MultiPill
          bgColor={multiPillBgColor}
          columnKey={columnKey}
          cursor={cursor}
          fontColor={multiPillColor}
          fullWidth={fullWidth}
          pillWidth={pillWidth}
          key={index}
          uppercase={uppercase}
          value={item}
        >
          {item}
        </MultiPill>
      )
    })
  }

  if (!isStatusPill) return <Fragment>{value}</Fragment>

  if (hoverLabel) {
    return (
      <Tooltip content={hoverLabel} forceShow position="right" tinyTooltip useAutoWidth>
        <Pill
          bgColor={bgColor}
          fontBold
          fontColor={color}
          fullWidth={fullWidth}
          value={value}
          uppercase={uppercase}
        >
          {value}
        </Pill>
      </Tooltip>
    )
  }

  return (
    <Pill
      bgColor={bgColor}
      fontBold={fontBold}
      fontColor={color}
      fullWidth={fullWidth}
      value={value}
      uppercase={uppercase}
    >
      {value}
    </Pill>
  )
}

const DashStatTableValueLink = styled(Link).withConfig({
  shouldForwardProp: prop => !['trimText'].includes(prop)
})`
  color: ${th('text.dark')};
  font-size: ${props => (props.trimText ? '12px' : 'inherit')};
  text-decoration: underline;
  white-space: ${props => (props.trimText ? 'nowrap' : 'normal')};
`

export const DashStatTableValue = styled.td.withConfig({
  shouldForwardProp: prop =>
    ![
      'fontSize',
      'size',
      'minWidthMobile',
      'primaryCell',
      'value',
      'collapse',
      'smallIcon',
      'vPadding',
      'cellAlign',
      'center',
      'nowrap',
      'minWidth',
      'trimText'
    ].includes(prop)
})`
  font-size: ${({ fontSize, size }) =>
    fontSize ? fontSize : ['small', 'smallButWrap'].includes(size) ? '12px' : '15px'};
  line-height: 1.35em;
  max-width: ${props => (props.trimText ? '200px' : 'none')};
  min-width: ${({ minWidthMobile, primaryCell, size, value, collapse }) => {
    if (minWidthMobile) return minWidthMobile
    if (primaryCell) return '150px'
    if (size === 'smallButWrap' && value) return '1%'
    if (size === 'smallButWrap' && !value) return '1%'
    if (!collapse && value) return '100px'
    if (collapse && value) return '1%'
    return '50px'
  }};
  overflow: ${props => (props.trimText ? 'hidden' : 'unset')};
  padding: ${({ smallIcon, vPadding }) => {
    if (smallIcon) return '3px 0 0'
    if (vPadding) return `${vPadding}px 10px`
    return '0 10px'
  }};
  text-align: ${({ cellAlign, center }) =>
    cellAlign === 'right' ? 'right' : center ? 'center' : 'left'};
  white-space: ${({ size, nowrap, collapse }) =>
    size === 'smallButWrap'
      ? 'normal'
      : nowrap || collapse || size === 'small'
        ? 'nowrap'
        : 'normal'};

  @media screen and (min-width: 769px) {
    min-width: ${({ minWidth, primaryCell, size, value, collapse }) => {
      if (minWidth) return minWidth
      if (primaryCell) return '250px'
      if (size === 'smallButWrap' && value) return '200px'
      if (size === 'smallButWrap' && !value) return '1%'
      if (!collapse && value) return '200px'
      if (collapse && value) return '1%'
      return 'auto'
    }};
  }
`

export const TableCellButtonWrapper = styled.div`
  margin: 10px 10px 10px 0;
  display: ${props => props.display || 'inline-block'};
`

export const BorderWrapper = styled.div`
  background: white;
  border: 1px solid ${th('borders.medium')};
  margin: 0;
  overflow: auto;
`

export const ResultsWrapper = styled.div.withConfig({
  shouldForwardProp: prop => !['dashboard', 'noResults'].includes(prop)
})`
  display: flex;
  flex-wrap: wrap;
  margin: 0;
  padding: ${props => (props.dashboard ? '5px 0' : '0 16px')};

  @media screen and (min-width: 600px) {
    overflow-x: hidden;
    margin: ${props => (props.dashboard ? '10px -10px' : '0')};
    padding: ${props => (props.dashboard ? '10px 0' : props.noResults ? '16px 6px' : '10px 6px')};
  }

  @media screen and (min-width: 969px) {
    margin: ${props => (props.dashboard ? '10px -0 0 -10px' : '0')};
  }
`

export const EmptyTableHeaderBlock = styled.th`
  font-size: 12px;
  min-width: 55px;
  padding: 15px;
  position: relative;
`

export const EditIconWrapper = styled.div.withConfig({
  shouldForwardProp: prop => !['iconSize'].includes(prop)
})`
  position: relative;

  ${props => {
    if (props.iconSize) {
      return css`
        svg {
          height: ${props => props.iconSize};
          width: ${props => props.iconSize};
        }
      `
    }

    return css`
      svg {
        height: 15px;
        width: 15px;
      }
    `
  }}
`

export const LoadingMessageWrapper = styled.div`
  border: 2px solid ${th('borders.medium')};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1.5em;
  margin: 0;
  min-height: 250px;
  padding: 20px;
  width: 100%;
`

export const NoResultsImage = styled.img`
  margin: 0;
  width: 100%;

  @media screen and (min-width: 769px) {
    width: ${props => props.width || '50%'};
  }
`

export const NoResultsButtonWrapper = styled.div`
  display: block;
  margin: 10px auto 0;
  text-align: center;
  width: max-content;

  > * {
    margin-bottom: 10px;
  }
`

export const NoResultsImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

export const SortablesOuterWrapper = styled.div`
  align-items: center;
  display: flex;
`

export const SortablesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 4px;

  svg {
    height: 10px;
    width: 10px;

    &:hover {
      background: rgba(55, 155, 117, 0.333);
      cursor: pointer;
      padding: 1px;
    }
  }
`

export const StyledChevronUp = styled(ChevronUp)`
  fill: none;
  stroke: currentColor;
  stroke-width: ${({ isActive }) => (isActive ? 1 : 0.5)};
`

export const StyledChevronDown = styled(ChevronDown)`
  fill: none;
  stroke: currentColor;
  stroke-width: ${({ isActive }) => (isActive ? 1 : 0.5)};
`

export const NoResultsMessageWrapper = styled.div`
  border: 2px solid ${th('borders.medium')};
  border-radius: 4px;
  line-height: 1.5em;
  margin: 0;
  padding: 20px;
  width: 100%;

  @media screen and (min-width: 769px) {
    margin: 0 ${props => (props.sideMargin ? '10px' : '0')};
  }
`

export const NoResultsMessageHeader = styled.h4`
  font-family: ${th('fonts.bold')};
  margin: 0;
  text-align: center;
`

export const NoResultsMessage = styled.div`
  line-height: 1.5em;
  margin: 0 auto;
  text-align: center;
  width: 80%;

  @media screen and (min-width: 769px) {
    margin: 0 auto;
    width: 50%;
  }
`

export const CardWrapper = styled.div.withConfig({
  shouldForwardProp: prop => !['cardType', 'showFilters'].includes(prop)
})`
  flex: ${props => determineCardFlex(props).default};
  margin: 0 0 10px;
  max-height: 100%;
  padding: 0;
  width: 0;

  @media screen and (min-width: 600px) {
    flex: ${props => determineCardFlex(props).w600};
    margin: 0 10px 10px;
  }

  @media screen and (min-width: 969px) {
    flex: ${props => determineCardFlex(props).w969};
  }
`

export const CornerIconWrapper = styled.div.withConfig({
  shouldForwardProp: prop => !['active'].includes(prop)
})`
  background: ${props => (props.active ? th('secondary.light') : 'white')};
  border: 1px solid ${props => (props.active ? th('secondary.dark') : th('borders.default'))};
  border-radius: 50%;
  height: 30px;
  margin: 10px 26px 8px 12px;
  padding: 4px;
  position: absolute;
  right: 0;
  width: 30px;

  &:hover {
    border: 1px solid ${th('primary.base')};
    background: ${th('transparent.base')};
    cursor: pointer;
  }
`
